// Pure helpers for the "Refer & earn" nav item (#276). Kept free of React so they unit-test
// without a render harness. The "NEW" hint is a per-viewer convenience: it lives in
// localStorage, and every read/write is guarded because storage can be blocked or empty.

export const REFER_NEW_DAYS = 14;
const FIRST_SEEN_KEY = 'bt-refer-nav-first-seen';
const VISITED_KEY = 'bt-refer-nav-visited';
const DAY_MS = 24 * 60 * 60 * 1000;

export interface ReferStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The browser's localStorage, or undefined when it is unavailable (private window, blocked). */
export function defaultReferStore(): ReferStore | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

function read(store: ReferStore | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: ReferStore | undefined, key: string, value: string): void {
  try {
    store?.setItem(key, value);
  } catch {
    /* storage blocked: the hint just won't persist */
  }
}

/** Records the first time the nav item was shown to this browser. No-op once set. */
export function markReferNavSeen(store: ReferStore | undefined, now: number): void {
  if (read(store, FIRST_SEEN_KEY) === null) write(store, FIRST_SEEN_KEY, String(now));
}

/** The user opened /refer: the hint is done for good. */
export function markReferVisited(store: ReferStore | undefined): void {
  write(store, VISITED_KEY, '1');
}

/** Show the "NEW" pill for the first 14 days after the item first appears, until the page is opened. */
export function showReferNewBadge(store: ReferStore | undefined, now: number): boolean {
  if (read(store, VISITED_KEY) === '1') return false;
  const firstSeen = Number(read(store, FIRST_SEEN_KEY));
  if (!Number.isFinite(firstSeen) || firstSeen <= 0) return true; // not recorded yet: this render is the first sighting
  return now - firstSeen < REFER_NEW_DAYS * DAY_MS;
}

/** The nav item exists only while the program is on and the viewer owns a share link. */
export function referNavVisible(data: { enabled?: boolean; shareUrl?: string } | undefined, isExtension: boolean): boolean {
  return !isExtension && !!data?.enabled && !!data.shareUrl;
}

/** Where a visitor came from: ?via=nav|menu|nudge|post_pay. Anything else is "direct". */
const KNOWN_VIA = ['nav', 'menu', 'nudge', 'post_pay'];
export function referralVia(search: string): string {
  const via = new URLSearchParams(search).get('via') ?? '';
  return KNOWN_VIA.includes(via) ? via : 'direct';
}
